import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  BarStack,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import If from '@components/if/if.tsx';
import ChartLoader from '@shared/components/charts/chart-loader/chart-loader.tsx';
import ChartEmpty from '@shared/components/charts/chart-empty/chart-empty.tsx';
import { StackedBarChartProps } from '@shared/models/charts/stacked-bar-chart.model.ts';

const StackedBarChart = <T extends Record<string, unknown>>({
  data,
  loading = false,
  config
}: StackedBarChartProps<T>) => {
  const {
    xAxisKey,
    layers,
    height = 300,
    barSize = 20,
    barRadius = 10,
    showGrid = true,
    yAxisFormatter,
    tooltipFormatter,
    xAxisFormatter
  } = config;

  const hasLegend = layers.some((layer) => layer.label);
  const labelMap = Object.fromEntries(
    layers.map((layer) => [
      String(layer.dataKey),
      layer.label ?? String(layer.dataKey)
    ])
  );

  return (
    <div
      className="bg-(--white) rounded-xl p-4 overflow-hidden relative"
      style={{
        boxShadow: '0 4px 12px var(--black-alpha-50)'
      }}
    >
      <If
        loading={loading}
        condition={!loading && data.length > 0}
        loadingContent={<ChartLoader height={height} />}
        emptyContent={<ChartEmpty height={height} />}
      >
        <div className="flex flex-col" style={{ height }}>
          {hasLegend && (
            <div className="flex items-center justify-center gap-4 mb-3 h-8.5 shrink-0">
              {layers.map(
                (layer) =>
                  layer.label && (
                    <div
                      key={String(layer.dataKey)}
                      className="flex items-center"
                    >
                      <div
                        className="w-3.5 h-3.5 rounded-[50%] mr-2"
                        style={{ backgroundColor: layer.color }}
                      />
                      <span className="text-[14px] font-normal text-(--blue-4)">
                        {layer.label}
                      </span>
                    </div>
                  )
              )}
            </div>
          )}

          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            >
              {showGrid && (
                <CartesianGrid vertical={false} stroke="var(--gray-7)" />
              )}
              <XAxis
                dataKey={xAxisKey as string}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
                tickFormatter={xAxisFormatter}
                tickMargin={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
                tickFormatter={yAxisFormatter}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  border: 'none',
                  boxShadow: '0 0 10px var(--black-alpha-100)'
                }}
                labelStyle={{ color: 'var(--dark-2)' }}
                // recharts 3 defaults `itemSorter` to 'name' (alphabetical by
                // dataKey); keep the original insertion order defined in `layers`.
                itemSorter={(item) =>
                  layers.findIndex(
                    (layer) => String(layer.dataKey) === String(item.dataKey)
                  )
                }
                formatter={(value, name) => {
                  const formatted = tooltipFormatter
                    ? tooltipFormatter(value as number)
                    : value;
                  return [formatted, labelMap[name as string]];
                }}
              />
              <BarStack stackId="stack" radius={barRadius}>
                {layers.map((layer) => (
                  <Bar
                    key={String(layer.dataKey)}
                    dataKey={String(layer.dataKey)}
                    fill={layer.color}
                    barSize={barSize}
                  />
                ))}
              </BarStack>
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </If>
    </div>
  );
};

export default StackedBarChart;
